// src/pages/brand-collaborations-client-stories/components/CaseStudyShowcase.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CaseStudyShowcase = () => {
  const [selectedCase, setSelectedCase] = useState(0);

  const caseStudies = [
    {
      id: 1,
      title: "Global Technology Corporation",
      subtitle: "Digital Transformation Campaign",
      challenge: "Modernizing brand perception while maintaining corporate trust and credibility across 50+ international markets with diverse cultural considerations.",
      solution: "Comprehensive visual strategy combining executive portraits, facility tours, product demonstrations, and employee testimonials with cultural sensitivity and brand consistency.",
      innovation: "Implemented real-time collaboration tools allowing global teams to review and approve content remotely, reducing production timeline by 60%.",
      results: {
        engagement: "450% increase in social media engagement",
        brandLift: "300% improvement in brand perception metrics",
        roi: "800% return on investment within 12 months",
        reach: "15M+ global impressions across all platforms"
      },
      timeline: "18 months",
      team: "12 specialists",
      image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=2069&auto=format&fit=crop",
      gallery: [
        "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800",
        "https://images.pixabay.com/photo/2017/08/10/02/05/people-2619817_960_720.jpg",
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=800&auto=format&fit=crop"
      ]
    },
    {
      id: 2,
      title: "Platinum Properties International",
      subtitle: "Luxury Development Portfolio",
      challenge: "Showcasing $2B+ luxury real estate portfolio while maintaining exclusivity and attracting ultra-high-net-worth individuals in competitive markets.",
      solution: "Cinematic property presentations combining aerial cinematography, architectural photography, lifestyle integration, and virtual reality experiences.",
      innovation: "Developed proprietary VR viewing system allowing international clients to experience properties remotely with 99% accuracy to physical viewing.",
      results: {
        sales: "250% increase in pre-construction sales",
        pricing: "30% premium pricing achievement",
        time: "60% reduction in sales cycle",
        satisfaction: "98% client satisfaction rating"
      },
      timeline: "24 months",
      team: "8 specialists",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      gallery: [
        "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800",
        "https://images.pixabay.com/photo/2016/11/29/09/37/architecture-1868667_960_720.jpg",
        "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?q=80&w=800&auto=format&fit=crop"
      ]
    },
    {
      id: 3,
      title: "Elite Corporate Events",
      subtitle: "Global Summit Documentation",
      challenge: "Documenting high-security corporate events with C-suite executives while maintaining discretion, capturing authentic moments, and delivering immediate content.",
      solution: "Discrete documentation strategy using specialized equipment, real-time editing capabilities, and seamless integration with security protocols.",
      innovation: "Created live-streaming capabilities with instant highlight reels, allowing remote stakeholders to participate and receive immediate content delivery.",
      results: {
        coverage: "360° comprehensive event documentation",
        delivery: "2-hour turnaround for highlight content",
        satisfaction: "99.8% executive approval rating",
        efficiency: "50% reduction in post-event content delivery"
      },
      timeline: "36 months",
      team: "15 specialists",
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop",
      gallery: [
        "https://images.pexels.com/photos/1709003/pexels-photo-1709003.jpeg?auto=compress&cs=tinysrgb&w=800",
        "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=800&auto=format&fit=crop",
        "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800"
      ]
    }
  ];

  const currentCase = caseStudies[selectedCase];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Case Study <span className="text-gradient-gold">Showcase</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            In-depth analysis of project challenges, creative solutions, technical innovations, 
            and measurable outcomes that demonstrate strategic value
          </p>
        </div>

        {/* Case Study Navigation */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex space-x-4">
            {caseStudies.map((study, index) => (
              <button
                key={study.id}
                onClick={() => setSelectedCase(index)}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
                  selectedCase === index
                    ? 'bg-accent text-white shadow-glow'
                    : 'bg-surface text-text-secondary hover:text-accent hover:bg-accent/10'
                }`}
              >
                Case Study {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Active Case Study */}
        <div className="animate-fade-in">
          {/* Case Overview */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-3xl font-bold text-text-primary mb-2">{currentCase.title}</h3>
              <p className="text-xl text-accent mb-6">{currentCase.subtitle}</p>
              
              {/* Project Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-surface rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Clock" size={16} className="text-accent" strokeWidth={2} />
                    <span className="text-text-secondary text-sm">Timeline</span>
                  </div>
                  <span className="font-bold text-text-primary">{currentCase.timeline}</span>
                </div>
                <div className="bg-surface rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Users" size={16} className="text-accent" strokeWidth={2} />
                    <span className="text-text-secondary text-sm">Team Size</span>
                  </div>
                  <span className="font-bold text-text-primary">{currentCase.team}</span>
                </div>
              </div>

              {/* Quick Results */}
              <div className="bg-accent/10 rounded-lg p-6">
                <h4 className="font-bold text-text-primary mb-4">Key Results</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(currentCase.results).slice(0, 4).map(([key, value], index) => (
                    <div key={index} className="flex items-center text-sm">
                      <Icon name="TrendingUp" size={14} className="text-accent mr-2" strokeWidth={2} />
                      <span className="text-text-secondary">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative">
              <Image
                src={currentCase.image}
                alt={currentCase.title}
                className="w-full h-96 object-cover rounded-lg shadow-elevation"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-lg"></div>
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Challenge */}
            <div className="bg-surface rounded-lg p-8">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mb-4">
                <Icon name="AlertCircle" size={24} className="text-accent" strokeWidth={2} />
              </div>
              <h4 className="font-bold text-text-primary text-lg mb-4">Project Challenge</h4>
              <p className="text-text-secondary leading-relaxed">{currentCase.challenge}</p>
            </div>

            {/* Solution */}
            <div className="bg-surface rounded-lg p-8">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mb-4">
                <Icon name="Lightbulb" size={24} className="text-accent" strokeWidth={2} />
              </div>
              <h4 className="font-bold text-text-primary text-lg mb-4">Creative Solution</h4>
              <p className="text-text-secondary leading-relaxed">{currentCase.solution}</p>
            </div>

            {/* Innovation */}
            <div className="bg-surface rounded-lg p-8">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mb-4">
                <Icon name="Zap" size={24} className="text-accent" strokeWidth={2} />
              </div>
              <h4 className="font-bold text-text-primary text-lg mb-4">Technical Innovation</h4>
              <p className="text-text-secondary leading-relaxed">{currentCase.innovation}</p>
            </div>
          </div>

          {/* Results Grid */}
          <div className="bg-background-dark rounded-lg p-8 mb-16">
            <h4 className="font-bold text-text-primary-inverse text-2xl mb-8 text-center">Measurable Outcomes</h4>
            <div className="grid md:grid-cols-4 gap-6">
              {Object.entries(currentCase.results).map(([key, value], index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-accent/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Award" size={24} color="#D4AF37" strokeWidth={2} />
                  </div>
                  <div className="text-accent font-bold text-lg mb-1">{value}</div>
                  <div className="text-text-secondary-inverse text-sm capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Gallery */}
          <div>
            <h4 className="font-bold text-text-primary text-2xl mb-8 text-center">Project Gallery</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {currentCase.gallery.map((image, index) => (
                <div key={index} className="relative group overflow-hidden rounded-lg">
                  <Image
                    src={image}
                    alt={`${currentCase.title} gallery ${index + 1}`}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Icon name="Eye" size={32} className="text-white" strokeWidth={2} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyShowcase;